import { FC, SyntheticEvent, useMemo, useState } from 'react';
import { PolarisIcon } from '@/exports/columnCommonComponents';
import { Accordion, AccordionDetails, AccordionSummary, styled, Typography } from '@mui/material';
import { FocusTheme } from '@/components/Focus/configs/theme';
import ReviewScheduleListRow from './ReviewScheduleListRow';
import { formatDateTimePartsLocal, getOccurrenceIso } from '@/components/Focus/utils/newScheduleAvails';
import { useAppSelector } from '@/redux/hooks';
import { selectLaunchList } from '@/redux/selectors/focusNewScheduleSelectors';
import { getResolvedVersionLaunchDisplayState } from '@/components/Focus/utils/incrementalLaunchList';
import { selectAllLanguagesMap } from '@/redux/selectors/constantsSelectors';
import { ScheduleAvailType, TitleEntity } from '@/redux/slices/slice_types';
import { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import { selectVersions } from '@/redux/selectors/orderPlacementSelectors';

export type Option = {
  label: string;
  value: string;
};

type VersionLaunchItem = {
  id: string;
  versionId: number;
  occurrenceStartDate: string;
  title: string;
  subTitle: string;
  versionChipLabel: string;
  parentEntityTypeChipLabel?: string;
  offsetValue: number;
  offsetUnit: string;
  territoryAddedValues: string[];
  territoryRemovedValues: string[];
  dubAddedValues: string[];
  dubRemovedValues: string[];
  subAddedValues: string[];
  subRemovedValues: string[];
};

export type ScheduleRow = {
  versionId: number;
  occurrenceStartDate: string;
  selectedTerritories: string[];
  selectedDubs: string[];
  selectedSubs: string[];
  selectedVersions: (TitleEntity | TitleEntityV2)['id'][];
};

export type ReviewScheduleListAccordionProps = {
  index: number;
  version: any;
  frequency: string;
  startDate: string;
  row?: ScheduleRow;
  territoryItems: Option[];
  dubItems: Option[];
  subItems: Option[];
  versionItems: (TitleEntity | TitleEntityV2)['id'][];
  updateGroupRoot: (keyVersionId: number, patch: Partial<Omit<ScheduleRow, 'versionId'>>) => void;
  onOpenDelete: (versionId: number) => void;
  onOpenAvailModal: (type: ScheduleAvailType, versionId: number) => void;
};

const formatOffsetUnit = (offsetValue: number, offsetUnit: string) => (offsetValue === 1 ? offsetUnit.replace(/s$/, '') : offsetUnit);

const ReviewScheduleListAccordion: FC<ReviewScheduleListAccordionProps> = ({
  index,
  version,
  frequency,
  startDate,
  row,
  territoryItems,
  dubItems,
  subItems,
  versionItems,
  updateGroupRoot,
  onOpenDelete
}) => {
  const launchList = useAppSelector(selectLaunchList);
  const allLanguagesMap = useAppSelector(selectAllLanguagesMap);
  const allSelectedVersions = useAppSelector(selectVersions);
  const [expanded, setExpanded] = useState(false);

  const keyVersionId = row?.versionId ?? version.id;
  const rowStartDateIso = row?.occurrenceStartDate ?? getOccurrenceIso(startDate, frequency, index);
  const { date, time } = useMemo(() => formatDateTimePartsLocal(rowStartDateIso), [rowStartDateIso]);

  const meta = version.lineage_meta;
  const title =
    meta?.parent_entity_type === 'movie'
      ? meta?.movie_name
      : `${meta?.series_name} ${typeof meta?.season_sequence_order === 'undefined' ? '' : `S${meta?.season_sequence_order} • `}E${meta?.episode_sequence_order}`;
  const subTitle = meta?.parent_entity_type === 'movie' ? `Series: ${meta?.series_name}` : meta?.episode_name;

  const versionLaunchList = useMemo<VersionLaunchItem[]>(() => {
    return (launchList ?? [])
      .filter((launch) => launch?.type === 'incremental')
      .filter((launch) => !(launch?.incrementalLaunchVersions ?? [])?.remove?.find((v) => v === version.id))
      .map((launch) => {
        const matchedVersion = allSelectedVersions.find((sv) => sv.id === version.id);

        const launchMeta = matchedVersion?.lineage_meta;
        const launchTitle =
          launchMeta?.parent_entity_type === 'movie'
            ? launchMeta?.movie_name
            : `${launchMeta?.series_name} ${
                typeof launchMeta?.season_sequence_order === 'undefined' ? '' : `S${launchMeta?.season_sequence_order} • `
              }E${launchMeta?.episode_sequence_order}`;
        const launchSubTitle = launchMeta?.parent_entity_type === 'movie' ? `Series: ${launchMeta?.series_name}` : launchMeta?.episode_name;

        const resolvedDisplayState = getResolvedVersionLaunchDisplayState({
          launch,
          launchList,
          initialReleaseDateIso: rowStartDateIso,
          originalSelectedTerritories: territoryItems.map((item) => item.value),
          originalSelectedDubs: dubItems.map((item) => item.value),
          originalSelectedSubs: subItems.map((item) => item.value),
          originalSelectedVersions: versionItems
        });

        const territoryDraft = launch.incrementalLaunchRightsDraftByTab?.territories;
        const audioDraft = launch.incrementalLaunchRightsDraftByTab?.audio;
        const timedtextDraft = launch.incrementalLaunchRightsDraftByTab?.timedtext;
        const versionDraft = launch.incrementalLaunchVersions;

        return {
          id: launch.id,
          versionId: matchedVersion?.id ?? version.id,
          occurrenceStartDate: resolvedDisplayState.occurrenceStartDateIso,
          title: launchTitle,
          subTitle: launchSubTitle,
          versionChipLabel: matchedVersion?.version_type || matchedVersion?.name,
          parentEntityTypeChipLabel: launchMeta?.parent_entity_type ?? undefined,
          offsetValue: resolvedDisplayState.offsetValue,
          offsetUnit: resolvedDisplayState.offsetUnit,
          territoryAddedValues: territoryDraft?.add ?? [],
          territoryRemovedValues: territoryDraft?.remove ?? [],
          dubAddedValues: audioDraft?.add ?? [],
          dubRemovedValues: audioDraft?.remove ?? [],
          subAddedValues: timedtextDraft?.add ?? [],
          subRemovedValues: timedtextDraft?.remove ?? [],
          versionAddedValues: versionDraft?.add ?? [],
          versionRemovedValues: versionDraft?.remove ?? []
        };
      });
  }, [dubItems, launchList, rowStartDateIso, subItems, territoryItems, versionItems, version.id]);

  const hasVersionLaunchList = versionLaunchList.length > 0;

  const handleAccordionChange = (_event: SyntheticEvent, isExpanded: boolean) => {
    if (!hasVersionLaunchList) return;
    setExpanded(isExpanded);
  };

  return (
    <StyledAccordion expanded={hasVersionLaunchList ? expanded : false} onChange={handleAccordionChange} disableGutters>
      <StyledAccordionSummary
        aria-disabled={!hasVersionLaunchList}
        expandIcon={
          <StyledExpandIcon isDisabled={!hasVersionLaunchList}>
            <PolarisIcon icon="down" width={18} />
          </StyledExpandIcon>
        }
      >
        <ReviewScheduleListRow
          occurrenceStartDate={rowStartDateIso}
          date={date}
          time={time}
          title={title}
          subTitle={subTitle}
          versionChipLabel={version.version_type || version.name}
          parentEntityTypeChipLabel={meta?.parent_entity_type ?? undefined}
          rightsRestrictionId={keyVersionId}
          availData={{
            territoryItems,
            dubItems,
            subItems
          }}
          onChangeDate={(iso) => updateGroupRoot(keyVersionId, { occurrenceStartDate: iso })}
          onDelete={() => onOpenDelete(version.id)}
          launchType="first-time"
        />
      </StyledAccordionSummary>
      {hasVersionLaunchList && (
        <StyledAccordionDetails>
          <StyledDetailsList>
            {versionLaunchList.map((versionLaunch) => {
              const formatted = formatDateTimePartsLocal(versionLaunch.occurrenceStartDate);

              return (
                <ReviewRowWrapper key={versionLaunch.id}>
                  <IncrementWrapper>
                    <StyledIncrement>
                      +{versionLaunch.offsetValue}
                      <br />
                      {formatOffsetUnit(versionLaunch.offsetValue, versionLaunch.offsetUnit)}
                    </StyledIncrement>
                  </IncrementWrapper>
                  <ReviewScheduleListRow
                    occurrenceStartDate={versionLaunch.occurrenceStartDate}
                    date={formatted.date}
                    time={formatted.time}
                    title={versionLaunch.title}
                    subTitle={versionLaunch.subTitle}
                    versionChipLabel={versionLaunch.versionChipLabel}
                    parentEntityTypeChipLabel={versionLaunch.parentEntityTypeChipLabel}
                    rightsRestrictionId={versionLaunch.versionId}
                    launchId={versionLaunch.id}
                    availData={{
                      territoryAddedValues: versionLaunch.territoryAddedValues,
                      territoryRemovedValues: versionLaunch.territoryRemovedValues,
                      dubAddedValues: versionLaunch.dubAddedValues,
                      dubRemovedValues: versionLaunch.dubRemovedValues,
                      subAddedValues: versionLaunch.subAddedValues,
                      subRemovedValues: versionLaunch.subRemovedValues,
                      allLanguagesMap
                    }}
                    launchType="incremental"
                  />
                </ReviewRowWrapper>
              );
            })}
          </StyledDetailsList>
        </StyledAccordionDetails>
      )}
    </StyledAccordion>
  );
};

export default ReviewScheduleListAccordion;

const StyledAccordion = styled(Accordion)`
  background-color: ${({ theme }) => theme.palette.background.default};

  &::before {
    display: none;
  }
`;

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  '&.MuiAccordionSummary-root': {
    backgroundColor: (theme as FocusTheme).palette.neutral[70],
    marginBottom: theme.spacing(0.5),
    minHeight: '80px',
    borderRadius: `${theme.shape.borderRadius}px`,
    paddingRight: theme.spacing(3)
  },
  '& .MuiAccordionSummary-content': {
    marginTop: 0,
    marginBottom: 0,
    order: 1,
    justifyContent: 'space-between'
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    alignItems: 'center',
    color: (theme as FocusTheme).palette.neutral[20]
  },
  '& .MuiAccordionSummary-expandIconWrapper svg': {
    transform: 'rotate(-90deg)'
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded svg': {
    transform: 'rotate(-90deg)'
  }
}));

const StyledAccordionDetails = styled(AccordionDetails)`
  &&.MuiAccordionDetails-root {
    padding: 0 !important;
  }
`;

const StyledExpandIcon = styled('span')<{ isDisabled: boolean }>`
  display: inline-flex;
  align-items: center;
  opacity: ${({ isDisabled }) => (isDisabled ? 0.35 : 1)};
`;

const StyledDetailsList = styled('div')`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.5)};
  padding-bottom: ${({ theme }) => theme.spacing(0.5)};
`;

const ReviewRowWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: stretch;
`;

const IncrementWrapper = styled('div')`
  width: 120px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const StyledIncrement = styled(Typography)`
  color: ${({ theme }) => theme.palette.primary.main};
  font-size: 16px;
  white-space: pre-line;
  width: 55%;
`;
